import React, { useState } from 'react';
import { memberService } from '../../services/api';
import { FiX, FiUserPlus, FiEdit2 } from 'react-icons/fi';
import './ManagedProfileForm.css';

const ManagedProfileForm = ({ onClose, onCreated, onUpdated, existingProfile }) => {
  const isEditMode = !!existingProfile;

  const [formData, setFormData] = useState({
    firstName: existingProfile?.firstName || '',
    lastName: existingProfile?.lastName || '',
    middleName: existingProfile?.middleName || '',
    maidenName: existingProfile?.maidenName || '',
    nickname: existingProfile?.nickname || '',
    dateOfBirth: existingProfile?.dateOfBirth ? existingProfile.dateOfBirth.split('T')[0] : '',
    gender: existingProfile?.gender || '',
    isDeceased: existingProfile?.isDeceased || false,
    deceasedDate: existingProfile?.deceasedDate ? existingProfile.deceasedDate.split('T')[0] : '',
    bio: existingProfile?.bio || '',
    currentCity: existingProfile?.currentCity || '',
    hometown: existingProfile?.hometown || '',
    occupation: existingProfile?.occupation || '',
    placeOfBirth: existingProfile?.placeOfBirth || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('First and last name are required.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...formData,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth + 'T12:00:00').toISOString() : null,
        deceasedDate: formData.isDeceased && formData.deceasedDate
          ? new Date(formData.deceasedDate + 'T12:00:00').toISOString()
          : null
      };

      let response;
      if (isEditMode) {
        response = await memberService.updateManagedProfile(existingProfile.memberID, payload);
      } else {
        response = await memberService.createManagedProfile(payload);
      }

      if (response.success) {
        if (isEditMode) {
          if (onUpdated) onUpdated(response.data);
          onClose();
        } else {
          setSuccessMessage(response.message);
          if (onCreated) onCreated(response.data);
        }
      } else {
        setError(response.message || 'Failed to save profile.');
      }
    } catch (err) {
      setError('Failed to save profile. Please try again.');
      console.error('Failed to save managed profile:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content managed-profile-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {isEditMode ? <><FiEdit2 /> Edit Profile</> : <><FiUserPlus /> Add Family Member</>}
          </h3>
          <button className="btn btn-ghost" onClick={onClose}><FiX /></button>
        </div>

        {successMessage ? (
          <div className="modal-body">
            <div className="managed-profile-success">
              <div className="success-icon">✅</div>
              <p>{successMessage}</p>
              <button className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {!isEditMode && (
                <p className="managed-profile-intro">
                  Add a family member who can't create their own account - a child too young
                  for email, or a relative who has passed away. An admin will review before
                  they appear in the directory.
                </p>
              )}

              {error && <div className="form-error-message">{error}</div>}

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">First Name *</label>
                  <input type="text" name="firstName" className="form-input"
                    value={formData.firstName} onChange={handleChange} autoFocus />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name *</label>
                  <input type="text" name="lastName" className="form-input"
                    value={formData.lastName} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Middle Name</label>
                  <input type="text" name="middleName" className="form-input"
                    value={formData.middleName} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Maiden Name</label>
                  <input type="text" name="maidenName" className="form-input"
                    value={formData.maidenName} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Nickname</label>
                  <input type="text" name="nickname" className="form-input"
                    value={formData.nickname} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Gender</label>
                  <select name="gender" className="form-input"
                    value={formData.gender} onChange={handleChange}>
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date of Birth</label>
                  <input type="date" name="dateOfBirth" className="form-input"
                    value={formData.dateOfBirth} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Place of Birth</label>
                  <input type="text" name="placeOfBirth" className="form-input"
                    value={formData.placeOfBirth} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Hometown</label>
                  <input type="text" name="hometown" className="form-input"
                    value={formData.hometown} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Current City</label>
                  <input type="text" name="currentCity" className="form-input"
                    value={formData.currentCity} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Occupation</label>
                  <input type="text" name="occupation" className="form-input"
                    value={formData.occupation} onChange={handleChange} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-checkbox">
                  <input type="checkbox" name="isDeceased"
                    checked={formData.isDeceased} onChange={handleChange} />
                  This person is deceased
                </label>
              </div>

              {formData.isDeceased && (
                <div className="form-group">
                  <label className="form-label">Date of Passing</label>
                  <input type="date" name="deceasedDate" className="form-input"
                    value={formData.deceasedDate} onChange={handleChange} />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">About</label>
                <textarea name="bio" className="form-input" rows={3}
                  placeholder="A little about this person..."
                  value={formData.bio} onChange={handleChange} />
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Create Profile')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ManagedProfileForm;